import { useMutation, useQueryClient } from '@tanstack/react-query';

import { secretService } from '@/services/secret-service';
import { pollForResourceGone } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { secretMutationKeys, secretQueryKeys } from './query-keys';

/**
 * Hook to delete a secret.
 * Waits until the read model no longer shows the secret, then invalidates the cache.
 */
export function useDeleteSecret(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: secretMutationKeys.deleteSecret(tenantId),
    mutationFn: async (secretId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');

      await secretService.deleteSecret(tenantId, secretId);
      await pollForResourceGone(() =>
        secretService.findSecret(tenantId, secretId)
      );
      return secretId;
    },
    onSuccess: (_, secretId) => {
      qc.invalidateQueries({
        queryKey: secretQueryKeys.list(tenantId),
        refetchType: 'all',
      });
      qc.removeQueries({
        queryKey: secretQueryKeys.detail(tenantId, secretId),
      });
    },
  });
}
